<script setup lang="ts">
import { ARTICLES } from '../content/articles'
import { VIDEOS } from '../content/videos'
import VideoGrid from '@/shared/components/VideoGrid.vue'
import ReminderSignup from '@/shared/components/ReminderSignup.vue'
import GlassCard from '@/shared/components/GlassCard.vue'
// Pixabay Content License (free commercial use, no attribution required).
import bannerPhoto from '@/assets/ror-condensers.jpg'
</script>

<template>
    <section id="learn" class="site-section alt">
        <div class="site-content">
            <span class="kicker" v-reveal>Free education</span>
            <h2 v-reveal>Learn before you decide</h2>
            <p class="muted">
                The more you know, the harder you are to upsell. That's the point.
            </p>

            <figure v-reveal class="banner glass">
                <img
                    :src="bannerPhoto"
                    alt="A row of aging air-conditioning condenser units along a brick building"
                    width="1280"
                    height="853"
                    loading="lazy"
                />
                <figcaption class="muted">
                    Systems rarely fail all at once — they fade. Knowing the signs is how you
                    avoid paying for the fade twice.
                </figcaption>
            </figure>

            <div class="articles">
                <GlassCard v-for="article in ARTICLES" :key="article.slug" class="article">
                    <h3>{{ article.title }}</h3>
                    <p class="muted">{{ article.summary }}</p>
                    <dl>
                        <div>
                            <dt>Typical cost</dt>
                            <dd>{{ article.repairCost }}</dd>
                        </div>
                        <div>
                            <dt>Replace signal</dt>
                            <dd>{{ article.replaceSignal }}</dd>
                        </div>
                    </dl>
                </GlassCard>
            </div>

            <h2 class="videos-heading">Watch: maintenance that pays for itself</h2>
            <VideoGrid :videos="VIDEOS" />

            <GlassCard class="reminders">
                <ReminderSignup />
            </GlassCard>
        </div>
    </section>
</template>

<style scoped lang="scss">
.alt {
    background: var(--site-bg-alt);
}

.articles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: var(--site-space-lg);
    margin: var(--site-space-lg) 0 var(--site-space-2xl);
}

.article {
    h3 {
        font-size: 1.1rem;
    }

    dl {
        margin: var(--site-space-md) 0 0;
        font-size: 0.85rem;

        div {
            display: flex;
            gap: var(--site-space-sm);
            padding: var(--site-space-xs) 0;
            border-top: 1px dashed var(--site-border);
        }

        dt {
            color: var(--site-text-muted);
            min-width: 7.5rem;
        }

        dd {
            margin: 0;
        }
    }
}

.banner {
    margin: var(--site-space-lg) 0 var(--site-space-xl);
    padding: var(--site-space-sm);

    img {
        display: block;
        width: 100%;
        height: clamp(180px, 32vw, 320px);
        object-fit: cover;
        border-radius: var(--site-radius-md);
    }

    figcaption {
        padding: var(--site-space-sm) var(--site-space-md) var(--site-space-xs);
        font-size: 0.9rem;
        text-align: center;
    }
}

.videos-heading {
    margin-top: var(--site-space-xl);
}

.reminders {
    margin-top: var(--site-space-2xl);
}
</style>
